import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import FirebaseService from 'services/FirebaseService';
import AuthService from 'services/AuthService';

const AUTH_USER = 'AUTH_USER';
const ACTIVE_EVENT_ID = 'ACTIVE_EVENT_ID';

const parseStoredUser = () => {
	try {
		const raw = localStorage.getItem(AUTH_USER);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
};

const getStoredActiveEventId = () => {
	try {
		return localStorage.getItem(ACTIVE_EVENT_ID) || null;
	} catch {
		return null;
	}
};

const setStoredActiveEventId = (eventId) => {
	try {
		if (eventId) {
			localStorage.setItem(ACTIVE_EVENT_ID, eventId);
			return;
		}
		localStorage.removeItem(ACTIVE_EVENT_ID);
	} catch {
		// ignora
	}
};

const normalizeMessage = (payload, fallback) => {
	if (Array.isArray(payload)) {
		return payload.join(', ');
	}

	if (typeof payload === 'string' && payload.trim()) {
		return payload;
	}

	return fallback;
};

const normalizeEvents = (user) => {
	if (!user || !Array.isArray(user.events)) {
		return [];
	}

	return user.events
		.map((item) => ({
			eventId: item.eventId || item?.event?.id || null,
			role: item.role || 'VIEWER',
			isActive: item.isActive !== false,
			event: item.event || null,
		}))
		.filter((item) => !!item.eventId);
};

const resolveActiveEventId = (events, storedActiveEventId) => {
	if (!Array.isArray(events) || events.length === 0) {
		return null;
	}

	if (
		storedActiveEventId &&
		events.some((item) => item.eventId === storedActiveEventId)
	) {
		return storedActiveEventId;
	}

	return events[0]?.eventId || null;
};

const resolveRedirect = ({ user, events, activeEventId }) => {
	const role = user?.role;
	const isPlatformAdmin = role === 'ADMIN';
	const isEventUser = role === 'MANAGER' || role === 'OPERATOR';

	if (isEventUser && activeEventId) {
		return `/app/events/${activeEventId}/dashboard`;
	}

	if (isEventUser && Array.isArray(events) && events.length > 1) {
		return '/app/events';
	}

	if (isPlatformAdmin) {
		return '/app/dashboards/default';
	}

	return null;
};

const buildSessionState = (sessionUser, fallbackUser = null) => {
	const user = sessionUser || fallbackUser || null;
	const events = normalizeEvents(user);
	const activeEventId = resolveActiveEventId(events, getStoredActiveEventId());
	const redirect = resolveRedirect({ user, events, activeEventId });

	setStoredActiveEventId(activeEventId);

	return {
		user,
		events,
		activeEventId,
		redirect,
	};
};

export const fetchMe = createAsyncThunk(
	'auth/me',
	async (_, { rejectWithValue }) => {
		try {
			const response = await AuthService.me();
			return response;
		} catch (err) {
			return rejectWithValue(
				err?.response?.data?.message ||
					err?.message ||
					'Não foi possível carregar a sessão'
			);
		}
	}
);

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: null,
	token:
		localStorage.getItem(AUTH_TOKEN) || localStorage.getItem('token') || null,
	user: parseStoredUser(),
	events: normalizeEvents(parseStoredUser()),
	activeEventId: getStoredActiveEventId(),
	sessionLoaded: false,
};

export const signIn = createAsyncThunk(
	'auth/login',
	async (data, { rejectWithValue }) => {
		const { email, password } = data;

		try {
			const response = await AuthService.login({ email, password });
			const { accessToken, user } = response;

			AuthService.setSession(accessToken, user);

			return { accessToken, user };
		} catch (err) {
			return rejectWithValue(
				err?.response?.data?.message || err?.message || 'Erro ao fazer login'
			);
		}
	}
);

export const signUp = createAsyncThunk(
	'auth/register',
	async (data, { rejectWithValue }) => {
		const { email, password, name } = data;

		try {
			await AuthService.register({ email, password, name });

			const loginResponse = await AuthService.login({ email, password });
			const { accessToken, user } = loginResponse;

			AuthService.setSession(accessToken, user);

			return { accessToken, user };
		} catch (err) {
			return rejectWithValue(
				err?.response?.data?.message || err?.message || 'Erro ao cadastrar'
			);
		}
	}
);

export const signOut = createAsyncThunk('auth/logout', async () => {
	try {
		if (FirebaseService?.signOutRequest) {
			await FirebaseService.signOutRequest();
		}
	} catch (e) {
		// ignora erro legado
	}

	AuthService.clearSession();
	setStoredActiveEventId(null);
	return true;
});

export const signInWithGoogle = createAsyncThunk(
	'auth/signInWithGoogle',
	async (_, { rejectWithValue }) => {
		try {
			const response = await AuthService.loginInOAuth();
			const { accessToken, user } = response;

			AuthService.setSession(accessToken, user);

			return { accessToken, user };
		} catch (err) {
			return rejectWithValue(
				err?.response?.data?.message ||
					err?.message ||
					'Login com Google não configurado'
			);
		}
	}
);

export const signInWithFacebook = createAsyncThunk(
	'auth/signInWithFacebook',
	async (_, { rejectWithValue }) => {
		try {
			const response = await AuthService.loginInOAuth();
			const { accessToken, user } = response;

			AuthService.setSession(accessToken, user);

			return { accessToken, user };
		} catch (err) {
			return rejectWithValue(
				err?.response?.data?.message ||
					err?.message ||
					'Login com Facebook não configurado'
			);
		}
	}
);

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			const accessToken = action.payload?.accessToken || action.payload || null;
			const session = buildSessionState(action.payload?.user, state.user);

			state.loading = false;
			state.token = accessToken;
			state.user = session.user;
			state.events = session.events;
			state.activeEventId = session.activeEventId;
			state.redirect = session.redirect;
			state.sessionLoaded = true;
			state.message = '';
			state.showMessage = false;
		},
		showAuthMessage: (state, action) => {
			state.message = normalizeMessage(action.payload, 'Erro de autenticação');
			state.showMessage = true;
			state.loading = false;
		},
		hideAuthMessage: (state) => {
			state.message = '';
			state.showMessage = false;
		},
		signOutSuccess: (state) => {
			state.loading = false;
			state.token = null;
			state.user = null;
			state.events = [];
			state.activeEventId = null;
			state.redirect = '/auth/login';
			state.sessionLoaded = false;
			state.message = '';
			state.showMessage = false;
			setStoredActiveEventId(null);
		},
		showLoading: (state) => {
			state.loading = true;
		},
		signInSuccess: (state, action) => {
			const session = buildSessionState(action.payload?.user, null);

			state.loading = false;
			state.token = action.payload?.accessToken || null;
			state.user = session.user;
			state.events = session.events;
			state.activeEventId = session.activeEventId;
			state.redirect = session.redirect;
			state.sessionLoaded = true;
			state.message = '';
			state.showMessage = false;
		},
		setActiveEvent: (state, action) => {
			const eventId = action.payload || null;
			state.activeEventId = eventId;
			state.redirect = resolveRedirect({
				user: state.user,
				events: state.events,
				activeEventId: eventId,
			});
			setStoredActiveEventId(eventId);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => {
				state.loading = true;
				state.sessionLoaded = false;
				state.redirect = null;
			})
			.addCase(signIn.fulfilled, (state, action) => {
				const session = buildSessionState(action.payload.user, null);

				state.loading = false;
				state.token = action.payload.accessToken;
				state.user = session.user;
				state.events = session.events;
				state.activeEventId = session.activeEventId;
				state.redirect = session.redirect;
				state.sessionLoaded = true;
				state.message = '';
				state.showMessage = false;
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = normalizeMessage(action.payload, 'Erro ao fazer login');
				state.showMessage = true;
				state.loading = false;
				state.token = null;
				state.user = null;
				state.events = [];
				state.activeEventId = null;
				state.redirect = null;
				state.sessionLoaded = false;
			})
			.addCase(fetchMe.pending, (state) => {
				state.loading = true;
			})
			.addCase(fetchMe.fulfilled, (state, action) => {
				const session = buildSessionState(action.payload, state.user);

				state.loading = false;
				state.user = session.user;
				state.events = session.events;
				state.activeEventId = session.activeEventId;
				state.redirect = session.redirect;
				state.sessionLoaded = true;
				state.message = '';
				state.showMessage = false;

				if (state.token && session.user) {
					AuthService.setSession(state.token, session.user);
				}
			})
			.addCase(fetchMe.rejected, (state, action) => {
				state.loading = false;
				state.message = normalizeMessage(
					action.payload,
					'Não foi possível carregar a sessão'
				);
				state.showMessage = true;
				state.sessionLoaded = true;
				state.redirect = null;
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false;
				state.token = null;
				state.user = null;
				state.events = [];
				state.activeEventId = null;
				state.redirect = '/auth/login';
				state.sessionLoaded = false;
				state.message = '';
				state.showMessage = false;
				setStoredActiveEventId(null);
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false;
				state.token = null;
				state.user = null;
				state.events = [];
				state.activeEventId = null;
				state.redirect = '/auth/login';
				state.sessionLoaded = false;
				setStoredActiveEventId(null);
			})
			.addCase(signUp.pending, (state) => {
				state.loading = true;
				state.sessionLoaded = false;
				state.redirect = null;
			})
			.addCase(signUp.fulfilled, (state, action) => {
				const session = buildSessionState(action.payload.user, null);

				state.loading = false;
				state.token = action.payload.accessToken;
				state.user = session.user;
				state.events = session.events;
				state.activeEventId = session.activeEventId;
				state.redirect = session.redirect;
				state.sessionLoaded = true;
				state.message = '';
				state.showMessage = false;
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = normalizeMessage(action.payload, 'Erro ao cadastrar');
				state.showMessage = true;
				state.loading = false;
			})
			.addCase(signInWithGoogle.pending, (state) => {
				state.loading = true;
				state.sessionLoaded = false;
				state.redirect = null;
			})
			.addCase(signInWithGoogle.fulfilled, (state, action) => {
				const session = buildSessionState(action.payload.user, null);

				state.loading = false;
				state.token = action.payload.accessToken;
				state.user = session.user;
				state.events = session.events;
				state.activeEventId = session.activeEventId;
				state.redirect = session.redirect;
				state.sessionLoaded = true;
				state.message = '';
				state.showMessage = false;
			})
			.addCase(signInWithGoogle.rejected, (state, action) => {
				state.message = normalizeMessage(
					action.payload,
					'Login com Google não configurado'
				);
				state.showMessage = true;
				state.loading = false;
			})
			.addCase(signInWithFacebook.pending, (state) => {
				state.loading = true;
				state.sessionLoaded = false;
				state.redirect = null;
			})
			.addCase(signInWithFacebook.fulfilled, (state, action) => {
				const session = buildSessionState(action.payload.user, null);

				state.loading = false;
				state.token = action.payload.accessToken;
				state.user = session.user;
				state.events = session.events;
				state.activeEventId = session.activeEventId;
				state.redirect = session.redirect;
				state.sessionLoaded = true;
				state.message = '';
				state.showMessage = false;
			})
			.addCase(signInWithFacebook.rejected, (state, action) => {
				state.message = normalizeMessage(
					action.payload,
					'Login com Facebook não configurado'
				);
				state.showMessage = true;
				state.loading = false;
			});
	},
});

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
	setActiveEvent,
} = authSlice.actions;

export default authSlice.reducer;